import { useState, useEffect } from "react";
import type { Project, ProjectSortOption } from "@/lib/types";
import { MATERIALS } from "@/lib/types";
import { api } from "@/lib/api";
import { ProjectCard } from "@/components/ProjectCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, SlidersHorizontal, Box, Flame, GitFork, TrendingUp, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

const sortOptions: { value: ProjectSortOption; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
	{ value: "newest", label: "Newest", icon: Clock },
	{ value: "top-rated", label: "Top Rated", icon: Flame },
	{ value: "most-forked", label: "Most Forked", icon: GitFork },
	{ value: "most-printed", label: "Most Printed", icon: TrendingUp },
];

export function BrowsePage() {
	const [projects, setProjects] = useState<Project[]>([]);
	const [loading, setLoading] = useState(true);
	const [sort, setSort] = useState<ProjectSortOption>("newest");
	const [material, setMaterial] = useState("all");
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search), 300);
		return () => clearTimeout(timer);
	}, [search]);

	useEffect(() => {
		setLoading(true);
		api.projects
			.list(sort, material === "all" ? undefined : material, debouncedSearch || undefined)
			.then(setProjects)
			.finally(() => setLoading(false));
	}, [sort, material, debouncedSearch]);

	return (
		<div className="space-y-6">
			{/* Hero */}
			<div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-500/10 via-red-500/5 to-transparent border border-border/50 p-6 sm:p-8">
				<div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 h-64 w-64 rounded-full bg-orange-500/10 blur-3xl" />
				<div className="relative">
					<div className="flex items-center gap-2 mb-2">
						<Box className="h-5 w-5 text-orange-400" />
						<span className="text-xs font-semibold uppercase tracking-widest text-orange-400">Community Hub</span>
					</div>
					<h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
						Discover & Share <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">3D Print Projects</span>
					</h1>
					<p className="mt-2 text-sm text-muted-foreground max-w-lg">
						Upload STL files with proven print settings. Fork configs, share results, and build your maker reputation.
					</p>
				</div>
			</div>

			{/* Filters */}
			<div className="space-y-3">
				<div className="flex flex-col sm:flex-row gap-3">
					<div className="relative flex-1">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
						<Input
							placeholder="Search projects, tags, materials..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="pl-9 bg-card/50"
						/>
					</div>
					<div className="flex items-center gap-1 bg-card/50 border border-border/50 rounded-lg p-1">
						{sortOptions.map((opt) => {
							const Icon = opt.icon;
							return (
								<Button
									key={opt.value}
									variant={sort === opt.value ? "secondary" : "ghost"}
									size="sm"
									onClick={() => setSort(opt.value)}
									className={cn("gap-1.5 text-xs", sort === opt.value && "font-medium")}
								>
									<Icon className="h-3.5 w-3.5" />
									<span className="hidden sm:inline">{opt.label}</span>
								</Button>
							);
						})}
					</div>
				</div>

				{/* Material Filter */}
				<div className="flex items-center gap-2 flex-wrap">
					<SlidersHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
					<Badge
						variant={material === "all" ? "default" : "outline"}
						className="cursor-pointer text-xs"
						onClick={() => setMaterial("all")}
					>
						All Materials
					</Badge>
					{MATERIALS.map((m) => (
						<Badge
							key={m}
							variant={material === m ? "default" : "outline"}
							className="cursor-pointer text-xs"
							onClick={() => setMaterial(m)}
						>
							{m}
						</Badge>
					))}
				</div>
			</div>

			{/* Grid */}
			{loading ? (
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
					{[1, 2, 3, 4, 5, 6].map((i) => (
						<div key={i} className="rounded-xl border border-border/50 bg-card/30 animate-pulse">
							<div className="aspect-[4/3] bg-muted/50 rounded-t-xl" />
							<div className="p-4 space-y-2">
								<div className="h-4 bg-muted/50 rounded w-3/4" />
								<div className="h-3 bg-muted/30 rounded w-full" />
								<div className="h-3 bg-muted/30 rounded w-1/2" />
							</div>
						</div>
					))}
				</div>
			) : projects.length === 0 ? (
				<div className="text-center py-16">
					<Box className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
					<p className="text-muted-foreground">No projects found</p>
					<p className="text-xs text-muted-foreground/60 mt-1">Try adjusting your filters</p>
				</div>
			) : (
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
					{projects.map((p) => (
						<ProjectCard key={p.id} project={p} />
					))}
				</div>
			)}
		</div>
	);
}
